import { useState } from "react";

import { PdfPasswordModal } from "@/components/ui/PdfPasswordModal";
import { ApiError } from "@/lib/api/client";

const PASSWORD_CODES = ["password_required", "password_invalid"] as const;
type PasswordCode = (typeof PASSWORD_CODES)[number];

type PasswordPromptState = {
  code: PasswordCode;
  fileName: string;
  resolve: (password: string | null) => void;
};

function extractPasswordCode(err: unknown): PasswordCode | null {
  if (!(err instanceof ApiError)) return null;
  const raw = err.fieldErrors.code;
  const code = Array.isArray(raw) ? raw[0] : raw;
  if (code === "password_required" || code === "password_invalid") return code;
  return null;
}

/**
 * Provides a password-prompt modal + a file upload wrapper that automatically
 * retries with a user-supplied password when the backend reports a
 * password-protected PDF (`password_required` / `password_invalid`).
 *
 * Usage:
 *   const { renderPasswordModal, uploadFileWithPasswordPrompt } = usePdfPasswordPrompt();
 *
 *   // In JSX:
 *   {renderPasswordModal()}
 *
 *   // Around any uploadApplicationDocument call:
 *   await uploadFileWithPasswordPrompt(file, (pdfPassword) =>
 *     uploadApplicationDocument(uuid, { ..., pdfPassword })
 *   );
 */
export function usePdfPasswordPrompt() {
  const [prompt, setPrompt] = useState<PasswordPromptState | null>(null);

  const promptForPassword = (code: PasswordCode, fileName: string): Promise<string | null> =>
    new Promise((resolve) => setPrompt({ code, fileName, resolve }));

  const handleSubmit = (password: string) => {
    const resolve = prompt?.resolve;
    setPrompt(null);
    resolve?.(password);
  };

  const handleCancel = () => {
    const resolve = prompt?.resolve;
    setPrompt(null);
    resolve?.(null);
  };

  /**
   * Wrap a single-file upload: if the backend returns password_required /
   * password_invalid, show the modal and retry. Throws on cancellation or
   * any non-password error.
   */
  const uploadFileWithPasswordPrompt = async (
    file: File,
    doUpload: (pdfPassword: string | null) => Promise<unknown>,
  ): Promise<unknown> => {
    const attempt = async (password: string | null): Promise<unknown> => {
      try {
        return await doUpload(password);
      } catch (err) {
        const code = extractPasswordCode(err);
        if (code) {
          const entered = await promptForPassword(code, file.name);
          if (!entered) {
            throw new Error("Upload cancelled: a password is required for this PDF.");
          }
          return attempt(entered);
        }
        throw err;
      }
    };
    return attempt(null);
  };

  const renderPasswordModal = () => (
    <PdfPasswordModal
      open={Boolean(prompt)}
      fileName={prompt?.fileName ?? ""}
      isInvalid={prompt?.code === "password_invalid"}
      onSubmit={handleSubmit}
      onCancel={handleCancel}
    />
  );

  return { uploadFileWithPasswordPrompt, renderPasswordModal };
}
